import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { coverScale } from '../artwork-texture.js';
import { getHourglass } from '../hourglass-assets.js';

// Hourglass - a photographed hourglass on a desk in a dark study, filling with
// sand over the track: full on top at the start, a complete pile below at the
// end; on a new track it turns over. Ambient, piano, neo-classical, slow jazz,
// meditation. Rules of kiosk-app/CLAUDE.md (R1-R17); danceability 0.18 =
// R9's calmest band: no pulse - the flow is set by the track's length, the
// energy over 16 bars only sways the stream a little. (The first, modelled
// version is kept beside this file as hourglass.js.avant-20261015.)
//
// THE PHOTOS (tools/build-hourglass.mjs): the scene with the empty hourglass,
// the same scene without it (the plate), the hourglass's mask, the glass's
// reflections (RGBA, premultiplied) and its inner radius per row, with the
// measured axis, neck, rotation centre and volume tables.
//
// ONE QUAD, by layers, per pixel of the photo:
//   a. the photo
//   b. the SAND, only inside the profile r(y) (|x - axis| <= r: never outside
//      the glass), in the frame of a glass seen slightly from above (k = sin
//      tilt): the top is the glass under min(F, A + r tan a) - a level with a
//      funnel over the neck -, the bottom a pile P - r tan a at 34 deg; their
//      visible top surfaces are found by sampling the depth across the column
//      (7 samples). Each section is a cylinder lit from the left like the
//      photo; the free surfaces take the grazing light; a fine grain with a few
//      grains glinting; near the walls the sand is compressed horizontally (the
//      curved glass is a lens), more where the profile slopes.
//   c. the glass's tint on the sand: x 0.9, darker toward the walls
//   d. the glass's REFLECTIONS, screened over the sand: what puts the sand
//      behind the glass (?hournoglass=1 drops them: the sand then sits in
//      front of the glass, the proof)
// The stream (1 px, R7: a 1 px line made of 1 px grains moving down, faster
// as they fall), a few grains bouncing at the pile's apex and small slides on
// its flanks are in layer b.
//
// THE VOLUMES are exact: the top holds V0 (1 - p), the bottom V0 p, inverted
// through the build's tables.
//
// THE TURN, on a new track: the hourglass (the photo in its mask) rotates
// 180 deg in 2 s (ease in-out) about the measured centre, the plate behind it;
// the sand turns with it and, in the glass's own frame, settles from the old
// fraction to all in the old bottom (now on top). The frame is symmetric but
// its reflections are on the wrong side once turned: a 0.5 s cross-fade to the
// original photo (not turned), the sand staying where it is. No duration
// (radio): 10 minutes, then a turn.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'hourglass',
  family: 'photographic',
  danceability: 0.18,
  requiresTier: 0,
  energyLevel: 0.08,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.05,
  tonality: 0.90,
  enabled: true
};

const FLIP_S = 2.0;
const FADE_S = 0.5;
const RADIO_S = 600;
const SAND = [0.30, 0.18, 0.065];         // linear: a light ochre at the photo's dim exposure (0.50 read as flat beige)
const FREE_BPM = 70;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const fragment = `
  precision highp float;
  uniform sampler2D uPhoto;
  uniform sampler2D uPlate;
  uniform sampler2D uMask;
  uniform sampler2D uRefl;
  uniform sampler2D uProfile;
  uniform vec2 uFit;
  uniform vec2 uImg;             // photo size, px
  uniform float uAxis;
  uniform float uNeckY;
  uniform float uGlassBot;
  uniform vec2 uCenter;
  uniform float uTanA;
  uniform float uK;              // the view's tilt (sin)
  uniform vec3 uLight;           // toward the light (x right, y up, z AWAY from the camera)
  uniform vec3 uSand;
  uniform float uTime;
  uniform float uSway;
  uniform float uAngle;          // the turn
  uniform float uFade;           // > 0: the cross-fade to the photo not turned
  uniform vec4 uTopA;            // state A: F, A (px above the neck), wall radius, wall height
  uniform vec4 uBotA;            // P (apex above the floor), wall radius, wall height, flow
  uniform vec4 uTopB;            // state B (the cross-fade's target)
  uniform vec4 uBotB;
  uniform vec2 uSlide;           // a slide: position down the flank (0..1, < 0 none), its angle
  uniform float uNoGlass;
  uniform float uShowProfile;
  varying vec2 vUv;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  // the inner radius at photo row y (px), its slope, and whether the row is glass
  vec3 profileAt(float y) {
    vec4 t = texture2D(uProfile, vec2(0.5, 1.0 - (y + 0.5) / uImg.y));
    return vec3((t.r * 255.0 * 256.0 + t.g * 255.0) / 64.0, t.b, t.a);
  }
  vec3 shadeN(vec3 n, vec2 q) {
    vec3 L = normalize(uLight);
    float dif = max(0.0, dot(n, L));
    float g = 0.93 + 0.14 * hash(floor(q));
    float glint = step(0.996, hash(floor(q) + floor(uTime * 0.4 + hash(floor(q)) * 7.0))) * smoothstep(0.55, 0.9, dif);
    return uSand * (0.06 + 1.05 * dif) * g + vec3(1.0, 0.86, 0.62) * glint * 0.30;
  }
  vec3 sideN(float Xe, float r) { float u = clamp(Xe / max(r, 1.0), -1.0, 1.0); return vec3(u, 0.0, -sqrt(max(0.0, 1.0 - u * u))); }

  // The sand at photo position q, for a state: rgb, coverage
  vec4 sandAt(vec2 q, vec4 top, vec4 bot) {
    vec3 pr = profileAt(q.y);
    if (pr.z < 0.5) return vec4(0.0);
    float r = pr.x;
    float X = q.x - uAxis;
    if (abs(X) > r) return vec4(0.0);                       // strictly inside the glass
    float u = X / max(r, 1.0);
    // the lens: the sand compressed horizontally near the walls, more where the profile slopes
    float Xe = X * (1.0 - 0.10 * u * u * (0.6 + 2.0 * pr.y));
    vec3 col = vec3(0.0); float cov = 0.0;
    if (q.y < uNeckY) {
      // --- the top: under min(F, A + r tan a). The visible point of a column is
      // the front-most (smallest Z: Z grows away from the camera) solution of
      // Yu = surface(r) + k Z - for the cone part a quadratic, for the flat part
      // a line - which keeps the shading continuous (sampled depths made bands).
      float Yu = uNeckY - q.y;
      float F = top.x, A = top.y, Rw = top.z, Sw = top.w;
      if (F > 0.5) {
        if (abs(Xe) < Rw) {
          float Zm = sqrt(max(0.0, Rw * Rw - Xe * Xe));
          float bestZ = 1e9; vec3 n = vec3(0.0, 1.0, 0.0);
          // the cone: (Yu - A) - k Z = t sqrt(X^2 + Z^2), with (Yu - A) - k Z >= 0
          float c = Yu - A, t = uTanA, k = uK;
          float qa = k * k - t * t, qb = -2.0 * c * k, qc = c * c - t * t * Xe * Xe;
          float disc = qb * qb - 4.0 * qa * qc;
          if (disc >= 0.0) {
            float sq = sqrt(disc);
            for (int j = 0; j < 2; j++) {
              float Z = (-qb + (j == 0 ? -sq : sq)) / (2.0 * qa);
              float rr = sqrt(Xe * Xe + Z * Z);
              if (abs(Z) <= Zm && c - k * Z >= 0.0 && A + rr * t <= F && Z < bestZ) { bestZ = Z; n = normalize(vec3(-t * Xe / max(rr, 1e-3), 1.0, -t * Z / max(rr, 1e-3))); }
            }
          }
          // the flat level: Yu = F + k Z, outside the funnel's radius
          float Zf = (Yu - F) / k;
          if (abs(Zf) <= Zm && sqrt(Xe * Xe + Zf * Zf) * t + A >= F && Zf < bestZ) { bestZ = Zf; n = vec3(0.0, 1.0, 0.0); }
          float hFront = min(F, A + Rw * t) - k * Zm;              // the near rim
          if (bestZ < 1e8) { cov = 1.0; col = shadeN(n, q) * 1.05; }
          else if (Yu < hFront) { cov = 1.0; col = shadeN(sideN(Xe, r), q); }
        } else if (Yu < Sw) { cov = 1.0; col = shadeN(sideN(Xe, r), q); }
      }
    } else {
      // --- the bottom: the pile P - r tan a above the floor, same front-most solution ---
      float Yf = uGlassBot - q.y;
      float P = bot.x, Rw = bot.y, Yw = bot.z;
      if (P > 0.5 && Yf >= 0.0) {
        if (abs(Xe) < Rw) {
          float Zm = sqrt(max(0.0, Rw * Rw - Xe * Xe));
          float t = uTanA, k = uK;
          // (P - Yf) + k Z = t sqrt(X^2 + Z^2), with (P - Yf) + k Z >= 0
          float c = P - Yf;
          float qa = k * k - t * t, qb = 2.0 * c * k, qc = c * c - t * t * Xe * Xe;
          float disc = qb * qb - 4.0 * qa * qc;
          float bestZ = 1e9; vec3 n = vec3(0.0, 1.0, 0.0); float rB = 0.0;
          if (disc >= 0.0) {
            float sq = sqrt(disc);
            for (int j = 0; j < 2; j++) {
              float Z = (-qb + (j == 0 ? -sq : sq)) / (2.0 * qa);
              float rr = sqrt(Xe * Xe + Z * Z);
              if (abs(Z) <= Zm && c + k * Z >= 0.0 && Z < bestZ) { bestZ = Z; rB = rr; n = normalize(vec3(t * Xe / max(rr, 1e-3), 1.0, t * Z / max(rr, 1e-3))); }
            }
          }
          float hFront = max(0.0, P - Rw * t) - k * Zm;
          if (bestZ < 1e8) {
            cov = 1.0; col = shadeN(n, q) * 1.05;
            if (uSlide.x >= 0.0) {
              float da = mod(atan(bestZ, Xe) - uSlide.y + 3.14159, 6.28318) - 3.14159;
              col += uSand * 0.30 * exp(-da * da / 0.06) * exp(-pow(rB / max(Rw, 1.0) - uSlide.x, 2.0) / 0.006);
            }
          } else if (Yf < hFront) { cov = 1.0; col = shadeN(sideN(Xe, r), q); }
        } else if (Yf < Yw) { cov = 1.0; col = shadeN(sideN(Xe, r), q); }
      }
    }
    // --- the stream: 1 px, grains moving down, faster as they fall ---
    if (cov < 0.5 && bot.w > 0.5) {
      float apexY = uGlassBot - bot.x;
      if (q.y > uNeckY && q.y < apexY) {
        float fall = q.y - uNeckY;
        float sw = uSway * (0.4 + 1.6 * fall / max(apexY - uNeckY, 1.0)) * sin(uTime * 0.9 + fall * 0.05);
        float d = abs(X - sw);
        float line = max(0.0, 1.0 - d);
        // grains: cells in sqrt(fall) moving at a constant rate = falling under gravity
        float g = sqrt(fall) * 2.4 - uTime * 9.0;
        float grain = step(0.45, hash(vec2(floor(g), 3.0)));
        float I = line * (0.30 + 0.70 * grain) * smoothstep(0.0, 6.0, fall);
        col += uSand * 1.5 * I; cov = max(cov, min(1.0, I));
      }
      // a few grains bouncing off the apex
      for (int b = 0; b < 5; b++) {
        float fb = float(b);
        float t = mod(uTime * 1.3 + fb * 0.37, 2.2) / 0.45;
        if (t > 1.0) continue;
        float dir = mod(fb, 2.0) < 0.5 ? -1.0 : 1.0;
        vec2 gp = vec2(uAxis + dir * (3.0 + 9.0 * t) * (0.6 + 0.4 * hash(vec2(fb, 1.0))), apexY - 6.0 * 4.0 * t * (1.0 - t) + 9.0 * t * uTanA * 0.5);
        float dd = length(q - gp);
        float I = max(0.0, 1.0 - dd) * 0.8;
        col += uSand * 1.5 * I; cov = max(cov, I);
      }
    }
    if (cov <= 0.0) return vec4(0.0);
    // c. the glass's tint, darker toward the walls (seen at a slant)
    col *= 0.9 - 0.22 * pow(abs(u), 4.0);
    return vec4(col, cov);
  }

  // one layer: the hourglass turned by ang about the centre, with its sand
  vec4 layer(vec2 p, float ang, vec4 top, vec4 bot) {
    vec2 d = p - uCenter;
    float c = cos(ang), s = sin(ang);
    vec2 q = uCenter + vec2(c * d.x + s * d.y, -s * d.x + c * d.y);   // rotate back
    vec2 tuv = vec2(q.x / uImg.x, 1.0 - q.y / uImg.y);
    float inside = step(0.0, tuv.x) * step(tuv.x, 1.0) * step(0.0, tuv.y) * step(tuv.y, 1.0);
    vec3 col = texture2D(uPhoto, tuv).rgb;
    float a = ang == 0.0 ? 1.0 : texture2D(uMask, tuv).r * inside;
    vec4 sand = sandAt(q, top, bot);
    if (sand.a > 0.0) {
      vec3 sc = mix(col, sand.rgb, sand.a);
      // d. the reflections, screened over the sand
      if (uNoGlass < 0.5) { vec4 rf = texture2D(uRefl, tuv); sc = sc + rf.rgb - sc * rf.rgb; }
      col = sc;
    }
    if (uShowProfile > 0.5) {
      vec3 pr = profileAt(q.y);
      if (pr.z > 0.5 && abs(abs(q.x - uAxis) - pr.x) < 0.8) col = vec3(1.0, 0.1, 0.1);
      if (abs(q.y - uNeckY) < 0.6 && abs(q.x - uAxis) < 30.0) col = vec3(1.0);
    }
    return vec4(col, a);
  }

  void main() {
    vec2 puv = (vUv - 0.5) * uFit + 0.5;
    vec2 p = vec2(puv.x * uImg.x, (1.0 - puv.y) * uImg.y);
    vec4 L0 = layer(p, uAngle, uTopA, uBotA);
    vec3 col;
    if (uAngle == 0.0) col = L0.rgb;
    else col = mix(texture2D(uPlate, puv).rgb, L0.rgb, L0.a);
    if (uFade > 0.0) {
      vec4 L1 = layer(p, 0.0, uTopB, uBotB);
      col = mix(col, L1.rgb, uFade);
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

let MAT = null;
function material() {
  if (MAT) return MAT;
  const u = {
    uPhoto: { value: null }, uPlate: { value: null }, uMask: { value: null }, uRefl: { value: null }, uProfile: { value: null },
    uFit: { value: new THREE.Vector2(1, 1) }, uImg: { value: new THREE.Vector2(1376, 768) },
    uAxis: { value: 1083 }, uNeckY: { value: 377 }, uGlassBot: { value: 617 }, uCenter: { value: new THREE.Vector2(1083, 387) },
    uTanA: { value: Math.tan(34 * Math.PI / 180) }, uK: { value: 0.087 }, uLight: { value: new THREE.Vector3(-0.8, 0.4, -0.45) },
    uSand: { value: new THREE.Color(SAND[0], SAND[1], SAND[2]) }, uTime: { value: 0 }, uSway: { value: 1 },
    uAngle: { value: 0 }, uFade: { value: 0 },
    uTopA: { value: new THREE.Vector4() }, uBotA: { value: new THREE.Vector4() }, uTopB: { value: new THREE.Vector4() }, uBotB: { value: new THREE.Vector4() },
    uSlide: { value: new THREE.Vector2(-1, 0) }, uNoGlass: { value: 0 }, uShowProfile: { value: 0 }
  };
  MAT = { material: new THREE.ShaderMaterial({ uniforms: u, vertexShader: quadVertex, fragmentShader: fragment, depthTest: false, depthWrite: false }), u };
  return MAT;
}

function invert(arr, v, decreasing) {
  const n = arr.length - 1;
  let lo = 0, hi = n;
  const ok = (i) => decreasing ? arr[i] >= v : arr[i] <= v;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (ok(m)) lo = m; else hi = m; }
  const a = arr[lo], b = arr[hi];
  return (lo + clamp01(b === a ? 0 : (v - a) / (b - a))) / n;
}

export class HourglassVisualizer extends Visualizer {
  constructor(options) {
    super();
    const hp = parseFloat(urlParam('hourp'));
    this._forcedP = Number.isFinite(hp) ? clamp01(hp) : null;
    this._debugFlip = urlParam('hourflip') === '1';
    this._noGlass = urlParam('hournoglass') === '1';
    this._showProfile = urlParam('hourprofile') === '1';
  }

  get postProcessing() { return { bloomStrength: 0.20, bloomThreshold: 0.88 }; }

  init(scene, camera) {
    this._scene = scene;
    this._assets = getHourglass();
    const m = material();
    this._u = m.u;
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._fit(renderer); };
    this._quad.visible = !!this._assets;        // without both photos: unavailable (withdrawn upstream)
    scene.add(this._quad);
    const u = this._u;
    if (this._assets) {
      const a = this._assets, man = a.manifest;
      u.uPhoto.value = a.photo; u.uPlate.value = a.plate; u.uMask.value = a.mask; u.uRefl.value = a.refl; u.uProfile.value = a.profile;
      u.uImg.value.set(man.size[0], man.size[1]);
      u.uAxis.value = man.axisX; u.uNeckY.value = man.neckY; u.uGlassBot.value = man.glassBot;
      u.uCenter.value.set(man.center[0], man.center[1]);
      u.uTanA.value = Math.tan(man.repose * Math.PI / 180); u.uK.value = man.tiltK;
      u.uLight.value.set(man.light[0], man.light[1], -man.light[2]);
      this._T = man.tables;
      this._inner = man.inner;
      this._glassTop = man.glassTop;
    }
    u.uNoGlass.value = this._noGlass ? 1 : 0;
    u.uShowProfile.value = this._showProfile ? 1 : 0;
    u.uAngle.value = 0; u.uFade.value = 0;
    this._time = 0;
    this._progress = null;
    this._lastTrack = undefined;
    this._radioStart = Date.now();
    this._beatSeconds = 60 / FREE_BPM;
    this._energy = 0.4;
    this._flip = null;                    // { at, from }
    this._nextSlide = 6 + Math.random() * 8; this._slideAt = -1e9; this._slideAng = 0;
    this._lastDebugFlip = 0;
    this._lastPaletteRef = undefined;
    this._state = null;
    if (this._assets) this._state = this._writeState(0, u.uTopA.value, u.uBotA.value);
  }

  _fit(renderer) {
    if (!this._assets) return;
    const sz = new THREE.Vector2();
    renderer.getDrawingBufferSize(sz);
    const a = sz.x / Math.max(1, sz.y), s = this._assets.manifest.size;
    this._u.uFit.value.copy(coverScale(a, s[0] / s[1]));
  }

  // --- the sand's state for a fallen fraction p ------------------------------------
  _rUp(h) { const i = Math.round(this._u.uNeckY.value - this._glassTop - h); return this._inner[Math.max(0, Math.min(this._inner.length - 1, i))]; }
  _rFloor(h) { const i = Math.round(this._u.uGlassBot.value - this._glassTop - h); return this._inner[Math.max(0, Math.min(this._inner.length - 1, i))]; }
  _writeState(p, top, bot) {
    const T = this._T, tanA = this._u.uTanA.value;
    const vTop = T.V0 * (1 - p), vBot = T.V0 * p;
    const lam = invert(T.top, vTop, true);
    const F = lam < 0.3 ? T.F0 : T.F0 * (1 - (lam - 0.3) / 0.7);
    const A = lam < 0.3 ? T.F0 * (1 - lam / 0.3) : 0;
    const P = invert(T.bot, vBot, false) * T.pMax;
    // the top surface meets the wall: h with min(F, A + r(h) tan a) = h
    const g = (h) => Math.min(F, A + this._rUp(h) * tanA) - h;
    let lo = 0, hi = T.topH;
    if (g(lo) <= 0) hi = 0;
    for (let i = 0; i < 40 && hi - lo > 0.05; i++) { const mm = (lo + hi) / 2; if (g(mm) > 0) lo = mm; else hi = mm; }
    const has = vTop > T.V0 * 0.001;
    const Sw = has ? lo : 0;
    top.set(has ? F : 0, A, this._rUp(Sw), Sw);
    // the pile meets the wall: h with P - r(h) tan a = h, or stands on the floor
    const gb = (h) => P - this._rFloor(h) * tanA - h;
    let RwB, YwB;
    if (gb(0) <= 0) { RwB = Math.min(this._rFloor(0), P / tanA); YwB = 0; }
    else { let a0 = 0, b0 = P; for (let i = 0; i < 40; i++) { const mm = (a0 + b0) / 2; if (gb(mm) > 0) a0 = mm; else b0 = mm; } YwB = a0; RwB = this._rFloor(a0); }
    const flow = vTop > T.V0 * 0.002 && vBot < T.V0 * 0.999 ? 1 : 0;
    bot.set(vBot > T.V0 * 0.001 ? P : 0, RwB, YwB, flow);
    return { p, vTop, vBot, V0: T.V0, F, A, P, Sw, RwB, YwB, flow };
  }

  _applyPalette(palette) {
    const v = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors[0] : null;
    const base = new THREE.Color(SAND[0], SAND[1], SAND[2]);
    if (v) {
      const c = new THREE.Color(v);
      const lb = 0.2126 * base.r + 0.7152 * base.g + 0.0722 * base.b, lc = Math.max(1e-3, 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
      base.lerp(c.multiplyScalar(lb / lc), 0.15);          // 15 %: R4 allows 30, but sand must stay sand
    }
    this._u.uSand.value.copy(base);
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  update(deltaTime, profile) {
    if (!this._assets) return;
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.trackId !== this._lastTrack) {
      if (this._lastTrack !== undefined) this._startFlip();
      this._lastTrack = profile.trackId; this._progress = null; this._radioStart = Date.now();
    }
    if (profile.playbackState !== 'PLAYING') return;        // frozen, never black (R11)
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    let target;
    if (this._forcedP != null) target = this._forcedP;
    else if (profile.duration > 0) target = clamp01(profile.progress || 0);
    else {
      if ((Date.now() - this._radioStart) / 1000 >= RADIO_S) { this._radioStart = Date.now(); this._startFlip(); }
      target = clamp01((Date.now() - this._radioStart) / 1000 / RADIO_S);
    }
    if (this._debugFlip && this._time - this._lastDebugFlip > 12) { this._lastDebugFlip = this._time; this._startFlip(); }
    if (this._progress == null || Math.abs(target - this._progress) > 0.5) this._progress = target;
    else this._progress += (target - this._progress) * (1 - Math.exp(-dt / 0.5));
    // the energy over 16 bars: the stream's sway only
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    this._beatSeconds = 60 / (bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : FREE_BPM);
    const director = profile.director || {};
    const bands = director.hasReliableGrid ? director.bands : null;
    const e = bands ? clamp01(((bands.low || 0) + (bands.mid || 0) + (bands.high || 0)) / 3) : 0.4;
    this._energy += (e - this._energy) * (1 - Math.exp(-dt / (64 * this._beatSeconds)));

    const u = this._u;
    let angle = 0, fade = 0, p = this._progress;
    if (this._flip) {
      const k = (this._time - this._flip.at) / FLIP_S;
      if (k < 1) {
        // the turn: in the glass's frame the sand settles from f to all in the old bottom
        angle = Math.PI * (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);
        p = this._flip.from + (1 - this._flip.from) * smooth01(k);
      } else if (k < 1 + FADE_S / FLIP_S) {
        // the cross-fade: the turned photo (its sand all in the local bottom = on top)
        // to the original photo with the same sand (all in its top)
        angle = Math.PI; p = 1;
        fade = smooth01((k - 1) / (FADE_S / FLIP_S));
        this._writeState(this._progress, u.uTopB.value, u.uBotB.value);
      } else { this._flip = null; }
    }
    u.uAngle.value = angle; u.uFade.value = fade;
    this._state = this._writeState(clamp01(p), u.uTopA.value, u.uBotA.value);
    if (!this._flip) { u.uTopB.value.copy(u.uTopA.value); u.uBotB.value.copy(u.uBotA.value); }
    // a slide on the pile's flank now and then (random, never on a beat)
    if (this._time > this._nextSlide) { this._slideAt = this._time; this._slideAng = Math.random() * Math.PI * 2; this._nextSlide = this._time + 7 + Math.random() * 12; }
    const ss = (this._time - this._slideAt) / 1.6;
    u.uSlide.value.set(ss >= 0 && ss <= 1 ? ss : -1, this._slideAng);
    u.uTime.value = this._time;
    u.uSway.value = 0.6 + 0.9 * this._energy;
  }

  _startFlip() { this._flip = { at: this._time, from: this._progress == null ? 1 : this._progress }; }

  get probe() {
    return Object.assign({ progress: this._progress, angle: this._u.uAngle.value, fade: this._u.uFade.value, flipping: !!this._flip }, this._state || {});
  }

  dispose() {
    this._quad.onBeforeRender = null;
    this._scene.remove(this._quad);
    this._quad.geometry.dispose();
    const u = this._u;
    u.uPhoto.value = u.uPlate.value = u.uMask.value = u.uRefl.value = u.uProfile.value = null;
    this._scene = null;
  }
}
